import React, { useState } from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import { Box, MenuItem, TextField } from "@mui/material"
import ModalImageProject from "../../../../pages/Dashboard/ModalImageProject"

import ContactField from "components/Contacts/ContactField"
import InvoiceNumericTextField from "components/Invoices/Invoice/InvoiceNumericTextField"
import { useAuthContext } from "context/auth/authContext"
import { useBrands } from "components/ProfileRefinament/hooks/useBrands"

const NO_BRAND = "__no_brand__"

export default function GeneralTab({
  localProject,
  setLocalProject,
  openAdd,
  setOpenAdd,
}) {
  const [openImage, setOpenImage] = useState(false)
  const { user } = useAuthContext()
  const organizationId = user?.["custom:organizationId"]
  const { data: brands, isLoading: isLoadingBrands } = useBrands(organizationId)
  const brandOptions = brands || []
  const assignedBrandStillExists = brandOptions.some(
    b => b.id === localProject.brandId,
  )
  const brandValue = assignedBrandStillExists ? localProject.brandId : NO_BRAND
  // An absent brand renders with the organization's default one, resolved
  // backend-side by planhopper-core libs/branding/loader.py. Naming it here
  // saves a trip to the brands settings; the generic label is kept for the
  // organization with no brands, whose documents use the account scalars.
  const defaultBrand = brandOptions.find(b => b.isDefault)
  const noBrandLabel = defaultBrand
    ? i18n.t("editProject.noBrandNamed", { name: defaultBrand.name })
    : i18n.t("editProject.noBrand")
  const image = localProject.projectImage || ""
  const handleOpenImage = e => {
    e.preventDefault()
    setOpenImage(true)
  }
  const handleImageChange = newImage => {
    setLocalProject(lp => ({ ...lp, projectImage: newImage }))
    setOpenImage(false)
  }

  const rawIva = localProject.iva
  const ivaValue =
    rawIva === "" || rawIva === null || rawIva === undefined
      ? null
      : Number(rawIva)
  const ivaForField = Number.isFinite(ivaValue) ? ivaValue : null

  const rawIgic = localProject.igic
  let igicForField = null
  if (rawIgic !== "" && rawIgic !== null && rawIgic !== undefined) {
    const asNum = typeof rawIgic === "number" ? rawIgic : Number(rawIgic)
    if (Number.isFinite(asNum)) igicForField = asNum / 100
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
        <Box sx={{ position: "relative", minWidth: 114 }}>
          <img
            src={image}
            alt={i18n.t("editProject.avatar")}
            className="rounded-circle"
            style={{
              width: 114,
              height: 114,
              filter: "drop-shadow(0px 6px 6px rgba(0, 0, 0, 0.08))",
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <TextField
            label={i18n.t("editProject.name")}
            value={localProject.name || ""}
            onChange={e =>
              setLocalProject(lp => ({ ...lp, name: e.target.value }))
            }
            fullWidth
            variant="outlined"
            size="small"
            margin="normal"
            sx={{ mb: 2 }}
          />

          <ContactField
            label={i18n.t("editProject.associatedClient")}
            value={localProject.client || null}
            size="small"
            onChange={(_event, newValue) => {
              setLocalProject(lp => ({
                ...lp,
                client: newValue ? newValue.SK : "",
              }))
            }}
          />
        </Box>
      </Box>
      <ModalImageProject
        openImage={openImage}
        setOpenImage={setOpenImage}
        image={image}
        setImage={handleImageChange}
        updateProject={() => {}}
        campos={localProject}
        tagsFinal={localProject.tags || []}
        refBoton={{ current: { className: "AddContact" } }}
      />
      <Box sx={{ display: "flex", gap: 2 }}>
        <InvoiceNumericTextField
          label={i18n.t("editProject.iva")}
          value={ivaForField}
          onCommit={n =>
            setLocalProject(lp => ({
              ...lp,
              iva: n,
            }))
          }
          emptyFallback={0}
          min={0}
          max={100}
          fullWidth
          variant="outlined"
          size="small"
          margin="normal"
          inputProps={{ min: 0, max: 100, step: 0.01 }}
        />
        <InvoiceNumericTextField
          label={i18n.t("editProject.igic")}
          value={igicForField}
          onCommit={n =>
            setLocalProject(lp => ({
              ...lp,
              igic: n == null ? null : Math.round(n * 100),
            }))
          }
          emptyFallback={0}
          min={0}
          max={100}
          fullWidth
          variant="outlined"
          size="small"
          margin="normal"
          inputProps={{ min: 0, max: 100, step: 0.01 }}
        />
      </Box>
      <TextField
        label={i18n.t("editProject.description")}
        value={localProject.description || ""}
        onChange={e =>
          setLocalProject(lp => ({ ...lp, description: e.target.value }))
        }
        fullWidth
        variant="outlined"
        size="small"
        margin="normal"
      />
      <TextField
        label={i18n.t("editProject.orgAddress")}
        value={localProject.orgAddress || ""}
        onChange={e =>
          setLocalProject(lp => ({ ...lp, orgAddress: e.target.value }))
        }
        fullWidth
        variant="outlined"
        size="small"
        margin="normal"
      />
      <TextField
        select
        label={i18n.t("editProject.brand")}
        value={brandValue}
        onChange={e =>
          setLocalProject(lp => ({
            ...lp,
            brandId: e.target.value === NO_BRAND ? null : e.target.value,
          }))
        }
        disabled={isLoadingBrands}
        fullWidth
        variant="outlined"
        size="small"
        margin="normal"
      >
        <MenuItem value={NO_BRAND}>{noBrandLabel}</MenuItem>
        {brandOptions.map(brand => (
          <MenuItem key={brand.id} value={brand.id}>
            {brand.name}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  )
}

GeneralTab.propTypes = {
  localProject: PropTypes.object.isRequired,
  setLocalProject: PropTypes.func.isRequired,
  openAdd: PropTypes.bool.isRequired,
  setOpenAdd: PropTypes.func.isRequired,
}
